import React, { Fragment, useState, useRef, useCallback } from "react";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { DocumentRegular, ImageRegular, EditRegular } from "@fluentui/react-icons";
import { Answer } from "../../components/Answer";
import styles from "./ChatMessageContainer.module.css";
import { highlightText } from "../../util/highlightText";
import {
  type ChatMessage,
  type Citation,
  type Feedback,
} from "../../api";

const [ASSISTANT, ERROR, USER] = ["assistant", "error", "user"];

/** Strip the [System datetime: ...] prefix injected for time-aware queries. */
const SYS_DATETIME_RE = /^\[System datetime:[^\]]+\]\n\n/;

export type ChatMessageContainerProps = {
  conversationId: string;
  fetchingConvMessages: boolean;
  answers: ChatMessage[];
  activeCardIndex: number | null;
  handleSpeech: (index: number, action: string) => void;
  onShowCitation: (citedDocument: Citation) => void;
  onFeedbackUpdate?: (messageId: string, feedback: Feedback) => void;
  isReadOnly?: boolean;
  /** Index in `answers` where new messages begin after a historical conversation was resumed. */
  resumedAtIndex?: number;
  /** Timestamp recorded when the historical conversation was opened. */
  sessionStartTime?: Date;
  /** Optional search query for find-in-chat highlighting */
  findQuery?: string;
  onSuggestedQuestionClicked?: (question: string) => void;
  /** Called when the user edits a message and confirms it. index is the position in `answers`. */
  onEditMessage?: (index: number, newContent: string) => void;
  /** Whether a response is currently being generated (disables the edit button). */
  isGenerating?: boolean;
};

export const ChatMessageContainer: React.FC<ChatMessageContainerProps> = (
  props
) => {
  const {
    conversationId,
    fetchingConvMessages,
    answers,
    handleSpeech,
    activeCardIndex,
    onShowCitation,
    onFeedbackUpdate,
    isReadOnly,
    resumedAtIndex,
    sessionStartTime,
    findQuery,
    onSuggestedQuestionClicked,
    onEditMessage,
    isGenerating,
  } = props;

  // Index of the message currently being edited (null = none)
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);

  const handleEditClick = useCallback((index: number, content: string) => {
    setEditingIndex(index);
    setEditValue(content.replace(/^\[System datetime:[^\]]+\]\n\n/, ''));
    setTimeout(() => {
      const el = editTextareaRef.current;
      if (el) {
        el.focus();
        el.selectionStart = el.selectionEnd = el.value.length;
      }
    }, 0);
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditingIndex(null);
    setEditValue("");
  }, []);

  const handleEditConfirm = useCallback((index: number) => {
    const trimmed = editValue.trim();
    if (trimmed && onEditMessage) {
      onEditMessage(index, trimmed);
    }
    setEditingIndex(null);
    setEditValue("");
  }, [editValue, onEditMessage]);

  const handleEditKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>, index: number) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleEditConfirm(index);
    } else if (e.key === "Escape") {
      handleEditCancel();
    }
  }, [handleEditConfirm, handleEditCancel]);

  const dividerLabel = sessionStartTime
    ? sessionStartTime.toLocaleString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric',
        year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : new Date().toLocaleDateString();

  // Find the index of the last assistant message to only show suggested questions there
  const lastAssistantIndex = answers.reduce(
    (lastIdx, msg, idx) => (msg.role === ASSISTANT ? idx : lastIdx),
    -1
  );

  // Index of the very last user message — the edit button is hidden there until a
  // response arrives (i.e. the last message is no longer a user message).
  const lastUserIndex = answers.reduce(
    (lastIdx, msg, idx) => (msg.role === USER ? idx : lastIdx),
    -1
  );
  // The last user message has a response when the final message in the list is NOT a user message.
  const lastUserHasResponse = answers.length > 0 && answers[answers.length - 1].role !== USER;

  return (
    <Fragment>
      {fetchingConvMessages && (
        <div className={styles.fetchMessagesSpinner}>
          <Spinner size={SpinnerSize.medium} />
        </div>
      )}
      {!fetchingConvMessages &&
        answers.map((answer, index) => (
          <React.Fragment key={`${answer?.role}-${index}`}>
            {/* Session-resume divider — rendered before the first new message after loading history.
               Guard resumedAtIndex > 0 to prevent the divider appearing at the very top when all
               messages are new (i.e. no historical messages preceded them). */}
            {resumedAtIndex !== undefined &&
              resumedAtIndex > 0 &&
              index === resumedAtIndex && (
                <div className={styles.sessionDivider} aria-label="New messages in this session">
                  <hr className={styles.sessionDividerLine} />
                  <span className={styles.sessionDividerLabel}>
                    {dividerLabel}
                  </span>
                  <hr className={styles.sessionDividerLine} />
                </div>
              )}
            {answer.role === USER ? (
              <div
                className={styles.chatMessageUser}
                key={`${answer?.role}-${index}`}
              >
                {/* Edit button — only visible when a response has already come through */}
                {!isReadOnly && onEditMessage && !isGenerating &&
                  (index !== lastUserIndex || lastUserHasResponse) &&
                  editingIndex !== index && (
                  <button
                    className={styles.editMessageBtn}
                    title="Edit message"
                    aria-label="Edit message"
                    onClick={() => handleEditClick(index, answer.content)}
                  >
                    <EditRegular />
                  </button>
                )}
                {editingIndex === index ? (
                  <div className={styles.chatMessageUserMessage}>
                    <textarea
                      ref={editTextareaRef}
                      className={styles.editMessageTextarea}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => handleEditKeyDown(e, index)}
                      rows={3}
                      aria-label="Edit your message"
                    />
                    <div className={styles.editMessageHint}>
                      Press <kbd>Enter</kbd> to send, <kbd>Shift+Enter</kbd> for new line, <kbd>Esc</kbd> to cancel
                    </div>
                  </div>
                ) : (
                  <div className={styles.chatMessageUserMessage}>
                    {answer.content.replace(SYS_DATETIME_RE, '')}
                    {answer.attachments && answer.attachments.length > 0 && (
                      <div className={styles.chatMessageAttachments}>
                        {answer.attachments.map((att) => (
                          <div key={att.id} className={styles.chatAttachmentItem}>
                            <span className={styles.chatAttachmentIcon}>
                              {att.mime_type.startsWith("image/") ? (
                                <ImageRegular />
                              ) : (
                                <DocumentRegular />
                              )}
                            </span>
                            <span className={styles.chatAttachmentName}>{att.file_name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {answer.date && !isNaN(new Date(answer.date).getTime()) && (
                      <div className={styles.chatMessageUserTimestamp}>
                        {new Date(answer.date).toLocaleString(undefined, {
                          weekday: 'short', month: 'short', day: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : answer.role === ASSISTANT || answer.role === ERROR ? (
              <div
                className={styles.chatMessageGpt}
                key={`${answer?.role}-${index}`}
              >
                <Answer
                    answer={{
                      conversationId: conversationId,
                      messageId: answer.id,
                      answer:
                        answer.role === ASSISTANT
                          ? answer.content
                          : "Sorry, an error occurred. Try refreshing the conversation or waiting a few minutes. If the issue persists, contact your system administrator. Error: " +
                          answer.content,
                      citations:
                        (answer.role === ASSISTANT)
                          ? answer.citations!
                          : [],
                      feedback:
                        (answer.role === ASSISTANT)
                          ? answer.feedback
                          : undefined,
                      proposedAnswers:
                        (answer.role === ASSISTANT && index === lastAssistantIndex && !isReadOnly)
                          ? answer.proposedAnswers
                          : undefined,
                    }}
                    onSpeak={handleSpeech}
                    isActive={activeCardIndex === index}
                    onCitationClicked={(c) => onShowCitation(c)}
                    index={index}
                    canCopy={ answer.role === ASSISTANT && answer.endTurn }
                    canDownload={ !isReadOnly && answer.role === ASSISTANT && answer.endTurn }
                    canGiveFeedback={ !isReadOnly ? answer.role === ASSISTANT && answer.endTurn : false}
                    onFeedbackUpdate={onFeedbackUpdate}
                    findQuery={findQuery}
                    timestamp={
                      answer.endTurn && answer.date && !isNaN(new Date(answer.date).getTime())
                        ? new Date(answer.date).toLocaleString(undefined, {
                            weekday: 'short', month: 'short', day: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })
                        : undefined
                    }
                    model={answer.role === ASSISTANT && answer.endTurn ? answer.model : undefined}
                    onSuggestedQuestionClicked={onSuggestedQuestionClicked}
                />
              </div>
            ) : null}
          </React.Fragment>
        ))}
    </Fragment>
  );
};
